import type { ColumnDef } from '@tanstack/react-table';
import { PROMOTION_PERMISSIONS } from '@/constants/permissions';
import type { PermissionCode } from '@/constants/permissions';
import {
  createActionsColumn,
  createSerialColumn,
  MemberNameCell,
  StackedCellText,
  StackedListCell,
  WrappingCellText,
} from '@/components/common/DataTable';
import { ActiveStatusBadge } from '@/components/common/ActiveStatusBadge';
import { HighlightText } from '@/components/common/HighlightText';
import { InstituteNumbers } from '@/components/common/InstituteNumbers';
import { MeetPeopleCell } from '@/components/common/MeetPeopleCell';
import { WorkTypeBadge } from '@/components/common/WorkTypeBadge';
import { RowEditDeleteButtons, RowViewLink } from '@/components/common/RowActions';
import { EMPTY_VALUE } from '@/constants/app.constants';
import { formatDisplayDate } from '@/utils/date';
import { formatNumber } from '@/utils/number';
import type {
  PromoAdjustmentUnit,
  PromoDistributionRow,
  PromoDrillParams,
  PromoInstitute,
  PromoItem,
  PromoItemType,
  PromoMember,
  UserPromo,
} from '../types/promotion.types';
import { PROMO_ADJUSTMENT_UNIT_DISPLAY } from '../constants/promotion.constants';
import { PromoZmInstitutesLink } from './PromoZmInstitutesLink';

const dash = (value: string | null | undefined): string =>
  value === null || value === undefined || value === '' ? EMPTY_VALUE : value;

const qty = (value: number) => <span className="tabular-nums">{formatNumber(value)}</span>;

export interface RowActions<TRow> {
  onEdit: (row: TRow) => void;
  onDelete: (row: TRow) => void;
}

function actionsColumn<TRow extends { name: string }>(
  actions: RowActions<TRow>,
  editRequires: PermissionCode,
  deleteRequires: PermissionCode,
): ColumnDef<TRow, unknown> {
  return createActionsColumn<TRow>((item) => (
    <RowEditDeleteButtons
      item={item}
      itemName={item.name}
      onEdit={actions.onEdit}
      onDelete={actions.onDelete}
      editRequires={editRequires}
      deleteRequires={deleteRequires}
    />
  ));
}

export function getPromoAdjustmentColumns(
  zmSearch: PromoDrillParams,
  searchText?: string,
): ColumnDef<PromoDistributionRow, unknown>[] {
  return [
    createSerialColumn<PromoDistributionRow>(),
    {
      accessorKey: 'name',
      header: 'Promotional Item',
      cell: ({ row }) => (
        <WrappingCellText className="font-medium">
          <HighlightText text={row.original.name} highlightedText={searchText} />
        </WrappingCellText>
      ),
    },
    {
      accessorKey: 'itemNo',
      header: 'Item Id / Item Code',
      cell: ({ row }) => (
        <StackedCellText
          primary={row.original.itemNo}
          primaryClassName="text-xs tabular-nums"
          secondary={row.original.itemCode}
        />
      ),
    },
    {
      accessorKey: 'typeName',
      header: 'Type',
      cell: ({ row }) => dash(row.original.typeName),
    },
    {
      accessorKey: 'distributedQty',
      header: 'Distributed Qty',
      cell: ({ row }) => qty(row.original.distributedQty),
    },
    createActionsColumn<PromoDistributionRow>((item) => (
      <RowViewLink
        to="/promotion/adjustment/$promoItemId"
        params={{ promoItemId: item.promoItemId }}
        search={zmSearch}
        label="View ZMs"
        aria-label={`ZMs who handed out ${item.name}`}
      />
    )),
  ];
}

export function getPromoMemberColumns(
  unit: PromoAdjustmentUnit,
  promoItemId: number,
  zmSearch: PromoDrillParams,
  searchText?: string,
): ColumnDef<PromoMember, unknown>[] {
  return [
    createSerialColumn<PromoMember>(),
    {
      accessorKey: 'name',
      header: 'ZM Name',
      cell: ({ row }) => (
        <MemberNameCell
          member={{
            name: row.original.name,
            hrEmpId: row.original.hrEmpId,
            mobile: row.original.userMobile,
          }}
          searchText={searchText}
        />
      ),
    },
    {
      accessorKey: 'designationName',
      header: PROMO_ADJUSTMENT_UNIT_DISPLAY[unit].memberWorkHeader,
      cell: ({ row }) => (
        <StackedCellText
          primary={dash(row.original.departmentName)}
          secondary={row.original.designationName}
        />
      ),
    },
    {
      accessorKey: 'thanaName',
      header: PROMO_ADJUSTMENT_UNIT_DISPLAY[unit].memberAreaHeader,
      cell: ({ row }) => (
        <StackedCellText
          primary={dash(row.original.divisionName)}
          secondary={row.original.districtName}
          tertiary={row.original.thanaName}
        />
      ),
    },
    {
      accessorKey: 'distributedQty',
      header: PROMO_ADJUSTMENT_UNIT_DISPLAY[unit].memberAmountHeader,
      cell: ({ row }) => qty(row.original.distributedQty),
    },
    createActionsColumn<PromoMember>((member) => (
      <PromoZmInstitutesLink
        unit={unit}
        promoItemId={promoItemId}
        zm={member}
        zmSearch={zmSearch}
      />
    )),
  ];
}

export function getPromoInstituteColumns(
  unit: PromoAdjustmentUnit,
  searchText?: string,
): ColumnDef<PromoInstitute, unknown>[] {
  const unitDisplay = PROMO_ADJUSTMENT_UNIT_DISPLAY[unit];
  const isDetailed = unitDisplay.hasDetailedInstituteCells;
  return [
    createSerialColumn<PromoInstitute>(),
    {
      accessorKey: 'instituteName',
      header: 'Institute',
      cell: ({ row }) => (
        <StackedCellText
          primary={
            <WrappingCellText>
              <HighlightText text={dash(row.original.instituteName)} highlightedText={searchText} />
            </WrappingCellText>
          }
          secondary={
            isDetailed ? (
              <InstituteNumbers
                code={row.original.instituteCode}
                eiin={row.original.instituteEiin}
                highlightedText={searchText}
              />
            ) : (
              row.original.instituteCode
            )
          }
        />
      ),
    },
    {
      accessorKey: 'instituteThana',
      header: unitDisplay.instituteAreaHeader,
      cell: ({ row }) => (
        <StackedCellText
          primary={dash(row.original.instituteDivision)}
          secondary={row.original.instituteDistrict}
          tertiary={row.original.instituteThana}
        />
      ),
    },
    {
      accessorKey: 'givenDate',
      header: 'Date',
      cell: ({ row }) => formatDisplayDate(row.original.givenDate),
    },
    {
      accessorKey: 'workTypeCode',
      header: isDetailed ? 'Work Type' : 'Work',
      cell: ({ row }) =>
        isDetailed ? (
          <WorkTypeBadge code={row.original.workTypeCode} />
        ) : (
          dash(row.original.workTypeCode)
        ),
    },
    {
      accessorKey: 'teacherMobiles',
      header: 'Received By',
      cell: ({ row }) =>
        isDetailed ? (
          <MeetPeopleCell meetPeople={row.original.meetPeople} highlightedText={searchText} />
        ) : (
          <StackedListCell listText={row.original.teacherMobiles} />
        ),
    },
    {
      accessorKey: 'distributedQty',
      header: unitDisplay.instituteAmountHeader,
      cell: ({ row }) => qty(row.original.distributedQty),
    },
  ];
}

export function getPromoItemColumns(
  searchText?: string,
  actions?: RowActions<PromoItem>,
): ColumnDef<PromoItem, unknown>[] {
  const columns: ColumnDef<PromoItem, unknown>[] = [
    createSerialColumn<PromoItem>(),
    {
      accessorKey: 'name',
      header: 'Promotion Name',
      cell: ({ row }) => (
        <WrappingCellText className="font-medium">
          <HighlightText text={row.original.name} highlightedText={searchText} />
        </WrappingCellText>
      ),
    },
    {
      accessorKey: 'legacyId',
      header: 'Item Id / Item Code',
      cell: ({ row }) => (
        <StackedCellText
          primary={row.original.legacyId ?? EMPTY_VALUE}
          primaryClassName="text-xs tabular-nums"
          secondary={row.original.code}
        />
      ),
    },
    { accessorKey: 'typeName', header: 'Type', cell: ({ row }) => dash(row.original.typeName) },
    {
      accessorKey: 'description',
      header: 'Item Description',
      cell: ({ row }) => <WrappingCellText>{dash(row.original.description)}</WrappingCellText>,
    },
    {
      accessorKey: 'bookEditionName',
      header: 'Edition',
      cell: ({ row }) => dash(row.original.bookEditionName),
    },
    {
      accessorKey: 'isActive',
      header: 'Active',
      cell: ({ row }) => <ActiveStatusBadge isActive={row.original.isActive} />,
    },
    {
      accessorKey: 'activeFrom',
      header: 'Active From',
      cell: ({ row }) => formatDisplayDate(row.original.activeFrom),
    },
    {
      accessorKey: 'activeTo',
      header: 'Active To',
      cell: ({ row }) => formatDisplayDate(row.original.activeTo),
    },
    {
      accessorKey: 'slNum',
      header: 'Sl Num',
      cell: ({ row }) => row.original.slNum ?? EMPTY_VALUE,
    },
  ];

  if (actions)
    columns.push(
      actionsColumn(
        actions,
        PROMOTION_PERMISSIONS.ITEM_LIST_EDIT,
        PROMOTION_PERMISSIONS.ITEM_LIST_DELETE,
      ),
    );
  return columns;
}

export function getPromoItemTypeColumns(
  searchText?: string,
  actions?: RowActions<PromoItemType>,
): ColumnDef<PromoItemType, unknown>[] {
  const columns: ColumnDef<PromoItemType, unknown>[] = [
    createSerialColumn<PromoItemType>(),
    {
      accessorKey: 'name',
      header: 'Type Name',
      cell: ({ row }) => (
        <WrappingCellText className="font-medium">
          <HighlightText text={row.original.name} highlightedText={searchText} />
        </WrappingCellText>
      ),
    },
    {
      accessorKey: 'code',
      header: 'Type Code',
      cell: ({ row }) => <span className="text-xs tabular-nums">{dash(row.original.code)}</span>,
    },
    {
      accessorKey: 'description',
      header: 'Type Description',
      cell: ({ row }) => <WrappingCellText>{dash(row.original.description)}</WrappingCellText>,
    },
    {
      accessorKey: 'isActive',
      header: 'Active',
      cell: ({ row }) => <ActiveStatusBadge isActive={row.original.isActive} />,
    },
    {
      accessorKey: 'activeFrom',
      header: 'Active From',
      cell: ({ row }) => formatDisplayDate(row.original.activeFrom),
    },
    {
      accessorKey: 'activeTo',
      header: 'Active To',
      cell: ({ row }) => formatDisplayDate(row.original.activeTo),
    },
    {
      accessorKey: 'itemCount',
      header: 'Items',
      cell: ({ row }) => qty(row.original.itemCount),
    },
    {
      accessorKey: 'slNum',
      header: 'Sl Num',
      cell: ({ row }) => row.original.slNum ?? EMPTY_VALUE,
    },
  ];

  if (actions)
    columns.push(
      actionsColumn(
        actions,
        PROMOTION_PERMISSIONS.ITEM_TYPE_EDIT,
        PROMOTION_PERMISSIONS.ITEM_TYPE_DELETE,
      ),
    );
  return columns;
}

export function getAssignmentColumns(): ColumnDef<UserPromo, unknown>[] {
  return [
    createSerialColumn<UserPromo>(),
    {
      accessorKey: 'name',
      header: 'User Name',
      cell: ({ row }) => (
        <WrappingCellText className="font-medium">{row.original.name}</WrappingCellText>
      ),
    },
    { accessorKey: 'hrEmpId', header: 'HR ID', cell: ({ row }) => dash(row.original.hrEmpId) },
    {
      accessorKey: 'assignDate',
      header: 'Assign Date',
      cell: ({ row }) => formatDisplayDate(row.original.assignDate),
    },
    {
      accessorKey: 'targetDateFrom',
      header: 'Target Date From',
      cell: ({ row }) => formatDisplayDate(row.original.targetDateFrom),
    },
    {
      accessorKey: 'targetDateTo',
      header: 'Target Date To',
      cell: ({ row }) => formatDisplayDate(row.original.targetDateTo),
    },
    {
      accessorKey: 'isActive',
      header: 'Active',
      cell: ({ row }) => <ActiveStatusBadge isActive={row.original.isActive} />,
    },
    { accessorKey: 'itemCount', header: 'Items', cell: ({ row }) => qty(row.original.itemCount) },
    { accessorKey: 'totalQty', header: 'Total Qty', cell: ({ row }) => qty(row.original.totalQty) },
  ];
}
